import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { CreateNotificacionRequest } from '@models/notificacion/createNotificacionRequest.model';
import { CreateResponseBase } from '@models/base/createResponseBase.model';
import { UpdateNotificacionRequest } from '@models/notificacion/updateNotificacionRequest.model';
import { NotificacionDto } from '@models/notificacion/notificacionDto.model';
import { isFalsy } from 'utility-types';

@Injectable({
  providedIn: 'root'
})
export class NotificacionesService {

  private readonly baseUrl = '/api/Notificacion';

  constructor(private readonly http: HttpClient) {
  }

  // ============================================
  // MÉTODOS CRUD BÁSICOS
  // ============================================

  /**
   * Crea una nueva notificación
   * @param createNotificacionRequest - Datos de la notificación a crear
   * @returns Observable con la respuesta de creación
   */
  create(createNotificacionRequest: CreateNotificacionRequest): Observable<CreateResponseBase> {
    return this.http.post<CreateResponseBase>(`${this.baseUrl}/Create`, createNotificacionRequest);
  }

  /**
   * Actualiza una notificación existente
   * @param updateNotificacionRequest - Datos de la notificación a actualizar
   * @returns Observable con la respuesta de actualización
   */
  update(updateNotificacionRequest: UpdateNotificacionRequest): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/Update`, updateNotificacionRequest);
  }

  /**
   * Obtiene todas las notificaciones
   * @returns Observable con array de notificaciones
   */
  getAll(): Observable<NotificacionDto[]> {
    return this.http.get<NotificacionDto[]>(`${this.baseUrl}/GetAll`);
  }

  /**
   * Obtiene una notificación específica por su ID
   * @param id - ID de la notificación a buscar
   * @returns Observable con la notificación encontrada
   */
  getById(id: number): Observable<NotificacionDto> {
    const params = new HttpParams().set('id', id.toString());
    return this.http.get<NotificacionDto>(`${this.baseUrl}/GetById`, { params });
  }

  /**
   * Elimina una notificación por su ID
   * @param id - ID de la notificación a eliminar
   * @returns Observable con la respuesta de eliminación
   */
  delete(id: number): Observable<any> {
    if (!isFalsy(id)) {
      const params = new HttpParams().set('id', id.toString());
      return this.http.delete<any>(`${this.baseUrl}/Delete`, { params });
    } else {
      return of(null);
    }
  }

  // ============================================
  // MÉTODOS ESPECÍFICOS DEL CONTROLLER
  // ============================================

  /**
   * Obtiene todas las notificaciones por tipo de notificación
   * @param idTipoNotificacion - ID del tipo de notificación
   * @returns Observable con array de notificaciones del tipo especificado
   */
  getAllByTipoNotificacion(idTipoNotificacion: number): Observable<NotificacionDto[]> {
    const params = new HttpParams().set('idTipoNotificacion', idTipoNotificacion.toString());
    return this.http.get<NotificacionDto[]>(`${this.baseUrl}/GetAllByTipoNotificacion`, { params });
  }

  /**
   * Marca como leídas las notificaciones especificadas
   * @param idsNotificaciones - Array de IDs de notificaciones a marcar como leídas
   * @returns Observable con la respuesta de la operación
   */
  setLecturaNotificaciones(idsNotificaciones: number[]): Observable<any> {
    return this.http.post(`${this.baseUrl}/SetLecturaNotificaciones`, idsNotificaciones);
  }

  // ============================================
  // MÉTODOS DE UTILIDAD
  // ============================================

  /**
   * Marca una sola notificación como leída
   * @param idNotificacion - ID de la notificación a marcar como leída
   * @returns Observable con la respuesta de la operación
   */
  marcarComoLeida(idNotificacion: number): Observable<any> {
    return this.setLecturaNotificaciones([idNotificacion]);
  }

  /**
   * Marca todas las notificaciones de un tipo como leídas
   * @param idTipoNotificacion - ID del tipo de notificación
   * @returns Observable con la respuesta de la operación
   */
  marcarTodasComoLeidasPorTipo(idTipoNotificacion: number): Observable<any> {
    return this.getAllByTipoNotificacion(idTipoNotificacion).pipe(
      // Podríamos hacer un switchMap aquí, pero es mejor obtener los IDs primero
      // y luego llamar a setLecturaNotificaciones
    );
  }

  /**
   * Obtiene el conteo de notificaciones no leídas por tipo
   * @param idTipoNotificacion - ID del tipo de notificación
   * @returns Observable con el número de notificaciones no leídas
   */
  getConteoNoLeidasPorTipo(idTipoNotificacion: number): Observable<number> {
    // Nota: Esto asume que NotificacionDto tiene una propiedad 'leida' o similar
    // Ajusta según tu modelo de datos
    return this.getAllByTipoNotificacion(idTipoNotificacion).pipe(
      map(notificaciones => notificaciones.filter(n => !n.estadoLectura).length)
    );
  }

  /**
   * Verifica si una notificación existe
   * @param id - ID de la notificación a verificar
   * @returns Observable boolean indicando si existe
   */
  exists(id: number): Observable<boolean> {
    return this.getById(id).pipe(
      // Devuelve true si la notificación existe, false si ocurre un error (por ejemplo, 404)
      map(notificacion => !!notificacion),
      catchError(() => of(false))
    );
  }
}
